import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Reveal } from '../motion';
import { BANKROLL, formatMoney } from '../demoData';

function Cell({ title, body, className = '', children, delay = 0 }) {
  return (
    <Reveal delay={delay} className={`pn-bezel ${className}`}>
      <div className="pn-plate flex h-full flex-col overflow-hidden p-[clamp(1.1rem,0.7rem+1.2*var(--u),1.9rem)]">
        <h3 className="pn-h3">{title}</h3>
        <p className="pn-body mt-2 max-w-[42ch]">{body}</p>
        <div className="mt-6 flex flex-1 items-end">{children}</div>
      </div>
    </Reveal>
  );
}

// Cumulative profit line plus per-session bars, drawn as the cell scrolls in.
function BankrollChart() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'center 55%'] });
  const grow = useTransform(scrollYProgress, [0, 1], [reduce ? 1 : 0, 1]);

  let run = 0;
  const cumulative = BANKROLL.map(v => (run += v));
  const max = Math.max(...cumulative, ...BANKROLL.map(Math.abs));
  const W = 600;
  const H = 320;
  const mid = H * 0.62;
  const scale = (H * 0.58) / max;
  const bw = W / BANKROLL.length;
  const line = cumulative.map((v, i) => `${i ? 'L' : 'M'}${(i + 0.5) * bw} ${mid - v * scale}`).join(' ');

  return (
    <div ref={ref} className="w-full">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="pn-caption">12 sessions · example data</span>
        <span className="pn-num text-[clamp(1.2rem,0.8rem+1.4*var(--u),2.2rem)] font-bold text-pn-signal">
          {formatMoney(run, { sign: true })}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" aria-hidden="true">
        <line x1="0" x2={W} y1={mid} y2={mid} stroke="rgb(var(--pn-line) / 0.15)" />
        {BANKROLL.map((v, i) => (
          <motion.rect
            key={i}
            x={i * bw + bw * 0.28}
            width={bw * 0.44}
            y={v >= 0 ? mid - v * scale : mid}
            height={Math.abs(v) * scale}
            rx="3"
            fill={v >= 0 ? 'rgb(var(--pn-signal) / 0.35)' : 'rgb(var(--pn-loss) / 0.45)'}
            style={{ scaleY: grow, transformOrigin: `0px ${mid}px`, transformBox: 'view-box' }}
          />
        ))}
        <motion.path d={line} fill="none" stroke="rgb(var(--pn-signal))" strokeWidth="2.5" strokeLinejoin="round" style={{ pathLength: grow }} />
      </svg>
    </div>
  );
}

// 24 weeks x 7 days; a session lands on Thursday each week.
function Calendar() {
  return (
    <div className="grid w-full grid-flow-col grid-rows-7 gap-[3px]" aria-hidden="true">
      {Array.from({ length: 24 * 7 }, (_, i) => {
        const week = Math.floor(i / 7);
        const day = i % 7;
        const v = day === 3 ? BANKROLL[week % BANKROLL.length] : null;
        const extra = day === 5 && week % 4 === 1; // occasional Saturday game
        const cls = v === null
          ? extra ? 'bg-pn-signal/25' : 'bg-pn-line/[0.06]'
          : v >= 0 ? (v > 15 ? 'bg-pn-signal' : 'bg-pn-signal/60') : 'bg-pn-loss/70';
        return <span key={i} className={`aspect-square rounded-[3px] ${cls}`} />;
      })}
    </div>
  );
}

function AliasMerge() {
  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      {['Jun', 'jun_2', 'Jun (phone)'].map(n => (
        <span key={n} className="rounded-full px-3 py-1.5 text-sm shadow-[inset_0_0_0_1px_rgb(var(--pn-line)/0.16)]">{n}</span>
      ))}
      <span className="px-1 text-pn-muted" aria-hidden="true">→</span>
      <span className="rounded-full bg-pn-signal px-3 py-1.5 text-sm font-bold text-pn-on-signal">Jun</span>
    </div>
  );
}

function Denomination() {
  return (
    <div className="flex w-full items-center justify-between rounded-2xl bg-pn-line/[0.05] p-1.5 text-sm">
      <span className="flex-1 rounded-xl px-3 py-2 text-center text-pn-muted">Cents</span>
      <span className="flex-1 rounded-xl bg-pn-raised px-3 py-2 text-center font-bold shadow-[inset_0_0_0_1px_rgb(var(--pn-line)/0.12)]">Dollars</span>
    </div>
  );
}

const SAVED = [
  { date: 'Sep 11', name: 'Thursday game', players: 6, net: 27.7 },
  { date: 'Sep 4', name: 'Thursday game', players: 7, net: -11.0 },
  { date: 'Aug 30', name: 'Saturday deep stack', players: 9, net: 15.25 },
];

function SavedLedgers() {
  return (
    <ul className="grid w-full gap-2 sm:grid-cols-3">
      {SAVED.map(g => (
        <li key={g.date} className="flex items-center justify-between rounded-2xl bg-pn-line/[0.04] px-4 py-3">
          <span>
            <span className="block font-bold">{g.name}</span>
            <span className="pn-caption">{g.date} · {g.players} players</span>
          </span>
          <span className={`pn-num font-bold ${g.net >= 0 ? 'text-pn-signal' : 'text-pn-loss'}`}>{formatMoney(g.net, { sign: true })}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Toolkit() {
  return (
    <section id="toolkit" aria-label="More tools" className="mx-auto max-w-[1480px] px-4 py-24 sm:px-6 tall:px-12 land:py-36">
      <Reveal>
        <h2 className="pn-h2 max-w-[16ch]">The rest of the toolkit.</h2>
      </Reveal>
      <div className="mt-12 grid gap-4 tall:grid-cols-2 land:grid-cols-4 land:grid-rows-[auto_auto_auto]">
        <Cell
          className="tall:col-span-2 land:col-span-2 land:row-span-2"
          title="Track your bankroll"
          body="Log every session. See cumulative profit, buy-ins against cash-outs, profit distribution and a session calendar."
        >
          <BankrollChart />
        </Cell>
        <Cell className="land:col-span-2" title="Session calendar" body="Every game you played, colored by result." delay={0.08}>
          <Calendar />
        </Cell>
        <Cell title="Merge duplicate names" body="Rejoined on a phone? Group aliases before you settle." delay={0.12}>
          <AliasMerge />
        </Cell>
        <Cell title="Cents or dollars" body="Match however your game counts chips." delay={0.16}>
          <Denomination />
        </Cell>
        <Cell className="tall:col-span-2 land:col-span-4" title="Saved ledgers" body="Every past game in one place, ready to reopen or share." delay={0.1}>
          <SavedLedgers />
        </Cell>
      </div>
    </section>
  );
}
